import React, { useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { EquipmentLabelService } from '../../services/fixedAssetCount.service';
import { LABEL_LAYOUT_OPTIONS, LabelLayout } from '../../interfaces/fixedAsset/fixedAssetCount.interface';

interface Props {
  open: boolean;
  equipmentIds: string[];
  onClose: () => void;
}

/** Picks the sticker sheet and where to start on it (a half-used sheet keeps its spare stickers), then downloads the PDF. */
const EquipmentLabelDialog: React.FC<Props> = ({ open, equipmentIds, onClose }) => {
  const toast = useToast();
  const [layout, setLayout] = useState<LabelLayout>('A4_3x7');
  const [startPosition, setStartPosition] = useState('1');
  const [busy, setBusy] = useState(false);

  const option = LABEL_LAYOUT_OPTIONS.find(o => o.value === layout)!;
  const start = Number(startPosition);
  const startOk = Number.isInteger(start) && start >= 1 && start <= option.perPage;

  const print = async () => {
    setBusy(true);
    try {
      await EquipmentLabelService.print({
        equipmentIds, layout, startPosition: option.perPage > 1 ? start : undefined,
      });
      toast.showSuccess('Labels downloaded');
      onClose();
    } catch (e) {
      toast.showApiError(e, 'Failed to print labels');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{ 'data-help-page': 'maint.labelDialog.page' } as any}>
      <DialogTitle>Print QR labels</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {equipmentIds.length} machine{equipmentIds.length === 1 ? '' : 's'} selected. Each label carries a QR code, the machine code, its name and serial number.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField select label="Sticker sheet" data-help="maint.labelDialog.field.layout" size="small" value={layout}
            onChange={e => { setLayout(e.target.value as LabelLayout); setStartPosition('1'); }}>
            {LABEL_LAYOUT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          {option.perPage > 1 && (
            <TextField label="Start at label number" data-help="maint.labelDialog.field.start" size="small" type="number" value={startPosition}
              onChange={e => setStartPosition(e.target.value)}
              error={!startOk} helperText={startOk ? `1 to ${option.perPage}. Skips stickers already used.` : `Enter 1 to ${option.perPage}`}
              inputProps={{ min: 1, max: option.perPage }} />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} data-help="maint.labelDialog.cancel">Cancel</Button>
        <Button variant="contained" onClick={print} data-help="maint.labelDialog.download" disabled={busy || equipmentIds.length === 0 || !startOk}>
          {busy ? 'Preparing...' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EquipmentLabelDialog;
